import { type VisibleWhen } from './definitions/screen';

function tokenize(path: string): string[] {
  return path.match(/[^.[\]]+/g) ?? [];
}

/** Reads `a.b[0].c` (or `a.b.0.c`) from an object; `undefined` when any segment is missing. */
export function resolvePath(obj: unknown, path: string | undefined): unknown {
  if (path === undefined || path === '' || path === '$') return obj;
  let current: unknown = obj;
  for (const token of tokenize(path)) {
    if (current === null || current === undefined) return undefined;
    if (Array.isArray(current)) {
      const index = Number(token);
      current = Number.isInteger(index) ? current[index] : undefined;
    } else if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[token];
    } else {
      return undefined;
    }
  }
  return current;
}

/** Replaces `{a.b}` / `{{a.b}}` placeholders with values from `context` (missing → empty string). */
export function interpolate(template: string, context: unknown): string {
  return template.replace(/\{\{?\s*([^{}\s]+)\s*\}?\}/g, (_, path: string) => {
    const value = resolvePath(context, path);
    return value === undefined || value === null ? '' : String(value);
  });
}

/**
 * True when a `{a.b}` placeholder of `template` has no value in `context`. Such a request can only be malformed
 * (`/receipts//summary`): a published screen extension shown where its record is not in context.
 */
export function hasUnresolvedPlaceholders(template: string, context: unknown): boolean {
  for (const m of template.matchAll(/\{\{?\s*([^{}\s]+)\s*\}?\}/g)) {
    const value = resolvePath(context, m[1]);
    if (value === undefined || value === null || value === '') return true;
  }
  return false;
}

/** Evaluates a `visibleWhen` condition against the screen context. */
export function evaluateCondition(condition: VisibleWhen | undefined, context: unknown): boolean {
  if (!condition) return true;
  const actual = resolvePath(context, condition.fact);
  switch (condition.op) {
    case 'eq':
      return actual === condition.value;
    case 'ne':
      return actual !== condition.value;
    case 'in':
      return Array.isArray(condition.value) ? condition.value.includes(actual) : false;
    case 'notNull':
      return actual !== null && actual !== undefined;
    case 'isNull':
      return actual === null || actual === undefined;
    default:
      return true;
  }
}
